// Shared helpers so every folder view (Inbox, Starred, Sent, …) can render
// the same Gmail-style rows / reading pane with identical sender parsing,
// avatars, previews and date formatting.

export const extractEmail = (from) => {
  const raw = String(from || '').trim();
  const angle = raw.match(/<([^>]+)>/);
  if (angle) return angle[1].trim().toLowerCase();
  const email = raw.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
  return email ? email[0].toLowerCase() : raw.toLowerCase();
};

export const parseSenderDisplay = (from, currentUserEmail) => {
  const raw = String(from || '').trim();
  if (!raw) return 'Unknown';

  // Gmail-style: self-sent mail shows "me" instead of your display name
  if (currentUserEmail) {
    const fromEmail = extractEmail(from);
    const me = String(currentUserEmail).trim().toLowerCase();
    if (fromEmail && me && fromEmail === me) return 'me';
  }

  const match = raw.match(/^(?:"([^"]+)"|([^<]+?))\s*<([^>]+)>$/);
  if (match) {
    const name = (match[1] || match[2] || '').trim();
    if (name) return name;
    return match[3].split('@')[0];
  }
  if (raw.includes('@')) {
    const name = raw.split('@')[0];
    return name.charAt(0).toUpperCase() + name.slice(1).replace(/[._-]/g, ' ');
  }
  return raw;
};

export const getSenderInitial = (from) => {
  const name = parseSenderDisplay(from);
  return (name.charAt(0) || '?').toUpperCase();
};

export const getAvatarColor = (from) => {
  const palettes = [
    { bg: '#e8f0fe', color: '#1967d2' },
    { bg: '#fce8e6', color: '#c5221f' },
    { bg: '#e6f4ea', color: '#137333' },
    { bg: '#fef7e0', color: '#b06000' },
    { bg: '#f3e8fd', color: '#9334e6' },
    { bg: '#e8eaed', color: '#5f6368' },
  ];
  const str = String(from || '');
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return palettes[Math.abs(hash) % palettes.length];
};

export const looksLikeHtml = (value) =>
  typeof value === 'string' && /<[a-z][\s\S]*>/i.test(value);

export const getPreviewText = (mail, max = 100) => {
  if (mail?.preview) return mail.preview;
  const raw = String(mail?.body || '');
  const text = raw
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > max ? `${text.substring(0, max)}...` : text;
};

export const formatMailDate = (dateString) => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const formatFullMailDate = (dateString) => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
